import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { LanguageSection } from "@/components/LanguageSection";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import { useTheme } from "@/context/ThemeContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import {
    Building2, Store, Users, Ruler, Tags, Boxes, Thermometer,
    ClipboardList, ChefHat, Plug, Palette, Languages, Plus, Trash2, Pencil, CalendarDays,
    Sun, Moon, Monitor,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const SECTIONS = [
    { key: "appearance", label: "Appearance", labelKey: "settings.appearance", icon: Palette, corporateOnly: false },
    { key: "language", label: "Language / Kieli", labelKey: "settings.languageTab", icon: Languages, corporateOnly: false },
    { key: "company", label: "Company", labelKey: "settings.company", icon: Building2, corporateOnly: true },
    { key: "restaurants", label: "Restaurants", labelKey: "common.restaurants", icon: Store, corporateOnly: true },
    { key: "units", label: "Units", labelKey: "settings.units", icon: Ruler, corporateOnly: false },
    { key: "categories", label: "Categories", labelKey: "settings.categories", icon: Tags, corporateOnly: false },
    { key: "areas", label: "Storage areas", labelKey: "settings.storageAreas", icon: Boxes, corporateOnly: false },
    { key: "equipment", label: "Equipment", labelKey: "settings.equipment", icon: Thermometer, corporateOnly: false },
    { key: "operating_days", label: "Operating Days & Daily Checks", labelKey: "settings.operatingDaysDailyChecks", icon: CalendarDays, corporateOnly: false },
    { key: "inventory", label: "Inventory settings", labelKey: "settings.inventorySettings", icon: ClipboardList, corporateOnly: true },
    { key: "recipes", label: "Recipe settings", labelKey: "settings.recipeSettings", icon: ChefHat, corporateOnly: true },
    { key: "integrations", label: "Integrations", labelKey: "settings.integrations", icon: Plug, corporateOnly: true },
    { key: "branding", label: "Branding", labelKey: "settings.branding", icon: Palette, corporateOnly: true },
];

export default function Settings() {
    const { t } = useTranslation();
    const { user } = useApp();
    const isCorporate = user?.role === "corporate";
    const visible = useMemo(
        () => SECTIONS.filter((s) => !s.corporateOnly || isCorporate),
        [isCorporate]);
    const [active, setActive] = useState(visible[0]?.key || "company");

    return (
        <div className="px-6 lg:px-12 py-10 max-w-6xl mx-auto">
            <PageHeader
                title={t("settings.settings", "Settings")}
                subtitle={t("settings.howIsThisCompanyAndRestaurant", "How is this company and restaurant configured?")}
                testId="settings-header"
            />
            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
                <nav className="space-y-1" data-testid="settings-tabs">
                    {visible.map((s) => (
                        <button key={s.key}
                            data-testid={`settings-tab-${s.key}`}
                            onClick={() => setActive(s.key)}
                            className={`w-full text-left px-3 h-10 inline-flex items-center gap-2 rounded-md text-sm font-medium transition ${
                                active === s.key
                                    ? "bg-neutral-900 text-white"
                                    : "text-neutral-700 hover:bg-neutral-100"
                            }`}
                        >
                            <s.icon className="h-4 w-4" /> {t(s.labelKey, s.label)}
                        </button>
                    ))}
                </nav>
                <div data-testid="settings-content">
                    {active === "appearance" && <AppearanceSection />}
                    {active === "language" && <LanguageSection />}
                    {active === "company" && <CompanySection />}
                    {active === "restaurants" && <RestaurantsSection />}
                    {active === "units" && <UnitsSection isCorporate={isCorporate} />}
                    {active === "categories" && <CategoriesSection isCorporate={isCorporate} />}
                    {active === "areas" && <AreasSection />}
                    {active === "equipment" && <EquipmentSection />}
                    {active === "operating_days" && <OperatingDaysSection />}
                    {active === "inventory" && <InventorySettingsSection />}
                    {active === "recipes" && <RecipeSettingsSection />}
                    {active === "integrations" && <IntegrationsSection />}
                    {active === "branding" && <BrandingSection />}
                </div>
            </div>
        </div>
    );
}

/* ---------- Reusable bits ---------- */

function SectionHeader({ title, action, description }) {
    return (
        <div className="flex items-start justify-between gap-3 mb-5">
            <div>
                <h2 className="font-display text-2xl font-bold">{title}</h2>
                {description && <p className="text-sm text-neutral-500 mt-1">{description}</p>}
            </div>
            {action}
        </div>
    );
}

function Field({ label, children, hint }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-wide font-semibold text-neutral-600 mb-1">{label}</label>
            {children}
            {hint && <p className="text-xs text-neutral-400 mt-1">{hint}</p>}
        </div>
    );
}

function Input({ testId, value, onChange, type = "text", placeholder = "" }) {
    return (
        <input data-testid={testId} type={type} value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm focus:outline-none focus:border-neutral-900" />
    );
}

function PrimaryButton({ onClick, children, testId, disabled }) {
    return (
        <button data-testid={testId} onClick={onClick} disabled={disabled}
            className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
            {children}
        </button>
    );
}

function GhostButton({ onClick, children, testId }) {
    return (
        <button data-testid={testId} onClick={onClick}
            className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-medium hover:bg-neutral-50">
            {children}
        </button>
    );
}

/* ---------- 1. Company ---------- */
function CompanySection() {
    const { t } = useTranslation();
    const { user } = useApp();
    const isSuperAdmin = user?.role === "super_admin";
    const [company, setCompany] = useState(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    useEffect(() => {
        api.get("/companies/me").then(({ data }) => {
            setCompany(data); setForm(data);
        });
    }, []);
    const save = async () => {
        try {
            await api.patch("/companies/me", form);
            toast.success(t("settings.companyUpdated", "Company updated"));
            setCompany(form); setEditing(false);
        } catch (err) {
            toast.error((err?.response?.data?.detail != null ? apiErrorMessage(err) : t("settings.couldNotUpdateCompany", "Could not update company")));
        }
    };
    if (!company) return <div className="kop-card p-6 text-sm text-neutral-500">{t("common.loading", "Loading…")}</div>;
    if (!editing) {
        return (
            <div data-testid="company-section">
                <SectionHeader title={t("settings.company", "Company")}
                    description={
                        isSuperAdmin
                            ? t("settings.topLevelEntityOneCompanyHolds", "Top-level entity. One company holds one or many restaurants.")
                            : t("settings.topLevelEntityCompanyLegalBilling", "Top-level entity. Company legal, billing and commercial fields are managed by the platform administrator.")
                    }
                    action={isSuperAdmin
                        ? <GhostButton onClick={() => setEditing(true)} testId="company-edit"><Pencil className="h-4 w-4" /> {t("common.edit", "Edit")}</GhostButton>
                        : null} />
                <div className="kop-card p-6 grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
                    {[
                        [t("common.name", "Name"), company.name],
                        [t("settings.businessId", "Business ID"), company.business_id],
                        [t("common.address", "Address"), company.address],
                        [t("common.email", "Email"), company.email],
                        [t("common.phone", "Phone"), company.phone],
                        [t("settings.subscriptionPlan", "Subscription plan"), company.subscription_plan],
                        [t("settings.defaultLanguage", "Default language"), company.default_language],
                        [t("settings.defaultCurrency", "Default currency"), company.default_currency],
                        [t("settings.defaultTimezone", "Default timezone"), company.default_timezone],
                    ].map(([k, v]) => (
                        <div key={k}>
                            <div className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">{k}</div>
                            <div className="mt-1 font-medium text-neutral-900">{v || "—"}</div>
                        </div>
                    ))}
                </div>
                {!isSuperAdmin && (
                    <div className="mt-3 text-xs text-neutral-500">
                        {t("settings.needToChangeTheseDetailsContact", "Need to change these details? Contact your MiseOne account manager.")}
                    </div>
                )}
            </div>
        );
    }
    return (
        <div data-testid="company-edit-form">
            <SectionHeader title={t("settings.editCompany", "Edit company")} />
            <div className="kop-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("common.name", "Name")}><Input testId="company-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} /></Field>
                <Field label={t("settings.businessId", "Business ID")}><Input testId="company-business_id" value={form.business_id} onChange={(v) => setForm({ ...form, business_id: v })} /></Field>
                <Field label={t("common.address", "Address")}><Input testId="company-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} /></Field>
                <Field label={t("common.email", "Email")}><Input testId="company-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} /></Field>
                <Field label={t("common.phone", "Phone")}><Input testId="company-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} /></Field>
                <Field label={t("settings.defaultLanguage", "Default language")}><Input testId="company-default_language" value={form.default_language} onChange={(v) => setForm({ ...form, default_language: v })} /></Field>
                <Field label={t("settings.defaultCurrency", "Default currency")}><Input testId="company-default_currency" value={form.default_currency} onChange={(v) => setForm({ ...form, default_currency: v })} /></Field>
                <Field label={t("settings.defaultTimezone", "Default timezone")}><Input testId="company-default_timezone" value={form.default_timezone} onChange={(v) => setForm({ ...form, default_timezone: v })} /></Field>
                <div className="md:col-span-2 flex gap-2 justify-end">
                    <GhostButton onClick={() => { setForm(company); setEditing(false); }} testId="company-cancel">{t("common.cancel", "Cancel")}</GhostButton>
                    <PrimaryButton onClick={save} testId="company-save">{t("common.saveChanges", "Save changes")}</PrimaryButton>
                </div>
            </div>
        </div>
    );
}

/* ---------- 2. Restaurants ---------- */
function RestaurantsSection() {
    const { t } = useTranslation();
    const { user } = useApp();
    const isSuperAdmin = user?.role === "super_admin";
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(null); // null | "create" | restaurant obj
    const load = async () => {
        const { data } = await api.get("/restaurants");
        setRows(data);
    };
    useEffect(() => { load(); }, []);
    return (
        <div data-testid="restaurants-section">
            <SectionHeader title={t("common.restaurants", "Restaurants")}
                description={isSuperAdmin
                    ? t("settings.aCompanyCanHaveOneOr", "A company can have one or many restaurants.")
                    : t("settings.restaurantsAreProvisionedByMiseoneContact", "Restaurants are provisioned by MiseOne. Contact your account manager to add or remove a restaurant.")}
                action={isSuperAdmin
                    ? <PrimaryButton onClick={() => setOpen("create")} testId="restaurant-new"><Plus className="h-4 w-4" /> {t("settings.newRestaurant", "New restaurant")}</PrimaryButton>
                    : null} />
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm" data-testid="restaurants-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{t("common.name", "Name")}</th>
                            <th className="px-5 py-3 font-semibold">{t("common.city", "City")}</th>
                            <th className="px-5 py-3 font-semibold">{t("common.phone", "Phone")}</th>
                            <th className="px-5 py-3 font-semibold">{t("settings.operation", "Operation")}</th>
                            {isSuperAdmin && <th className="px-5 py-3"></th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`restaurant-row-${r.id}`}>
                                <td className="px-5 py-3 font-medium">{r.name}</td>
                                <td className="px-5 py-3 text-neutral-600">{r.city}</td>
                                <td className="px-5 py-3 text-neutral-600">{r.phone || "—"}</td>
                                <td className="px-5 py-3 text-neutral-600 capitalize">{(r.operation_type || "").replace("_", " ")}</td>
                                {isSuperAdmin && (
                                    <td className="px-5 py-3 text-right">
                                        <GhostButton onClick={() => setOpen(r)} testId={`restaurant-edit-${r.id}`}><Pencil className="h-4 w-4" /></GhostButton>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <RestaurantDialog open={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />
        </div>
    );
}

function RestaurantDialog({ open, onClose, onSaved }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        name: "", city: "", address: "", phone: "", email: "",
        operation_type: "lunch_restaurant",
    });
    useEffect(() => {
        if (open === "create") setForm({ name: "", city: "", address: "", phone: "", email: "", operation_type: "lunch_restaurant" });
        else if (open) setForm({
            name: open.name || "", city: open.city || "", address: open.address || "",
            phone: open.phone || "", email: open.email || "",
            operation_type: open.operation_type || "lunch_restaurant",
        });
    }, [open]);
    const save = async () => {
        if (!form.name.trim()) { toast.error(t("settings.nameIsRequired", "Name is required")); return; }
        try {
            if (open === "create") await api.post("/restaurants", form);
            else await api.patch(`/restaurants/${open.id}`, form);
            toast.success(t("settings.restaurantSaved", "Restaurant saved"));
            onSaved();
        } catch { toast.error(t("settings.couldNotSaveRestaurant", "Could not save restaurant")); }
    };
    return (
        <Dialog open={!!open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="restaurant-dialog" className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{open === "create" ? t("settings.newRestaurant", "New restaurant") : t("settings.editRestaurant", "Edit restaurant")}</DialogTitle>
                    <DialogDescription>{t("settings.setTheDetailsForThisRestaurant", "Set the details for this restaurant location.")}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label={t("settings.name", "Name *")}><Input testId="rest-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} /></Field>
                    <Field label={t("common.city", "City")}><Input testId="rest-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} /></Field>
                    <Field label={t("common.address", "Address")}><Input testId="rest-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} /></Field>
                    <Field label={t("common.phone", "Phone")}><Input testId="rest-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} /></Field>
                    <Field label={t("common.email", "Email")}><Input testId="rest-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" /></Field>
                    <Field label={t("settings.operationType", "Operation type")}>
                        <select data-testid="rest-operation_type" value={form.operation_type}
                            onChange={(e) => setForm({ ...form, operation_type: e.target.value })}
                            className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white">
                            {["lunch_restaurant", "dinner_restaurant", "hotel", "cafe", "catering", "school_canteen", "other"].map((v) => (
                                <option key={v} value={v}>{v.replace(/_/g, " ")}</option>
                            ))}
                        </select>
                    </Field>
                </div>
                <DialogFooter>
                    <button data-testid="dialog-cancel-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("common.cancel", "Cancel")}</button>
                    <PrimaryButton onClick={save} testId="rest-save">{t("common.save", "Save")}</PrimaryButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ---------- 3. Users ---------- */
function UsersSection() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(null);
    const [restaurants, setRestaurants] = useState([]);
    const load = async () => {
        const [{ data }, { data: r }] = await Promise.all([
            api.get("/users"),
            api.get("/restaurants"),
        ]);
        setRows(data); setRestaurants(r);
    };
    useEffect(() => { load(); }, []);
    return (
        <div data-testid="users-section">
            <SectionHeader title={t("settings.users", "Users")}
                description={t("settings.twoRolesCorporateManagesCompanyAnd", "Two roles: Corporate (manages company) and Restaurant (one workstation).")}
                action={<PrimaryButton onClick={() => setOpen("create")} testId="user-new"><Plus className="h-4 w-4" /> {t("settings.newUser", "New user")}</PrimaryButton>} />
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm" data-testid="users-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{t("common.name", "Name")}</th>
                            <th className="px-5 py-3 font-semibold">{t("common.email", "Email")}</th>
                            <th className="px-5 py-3 font-semibold">{t("common.role", "Role")}</th>
                            <th className="px-5 py-3 font-semibold">{t("common.restaurant", "Restaurant")}</th>
                            <th className="px-5 py-3 font-semibold">{t("common.status", "Status")}</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                <td className="px-5 py-3 font-medium">{u.name}</td>
                                <td className="px-5 py-3 text-neutral-600">{u.email}</td>
                                <td className="px-5 py-3 text-neutral-600 capitalize">{u.role}</td>
                                <td className="px-5 py-3 text-neutral-600">{u.restaurant_name || "—"}</td>
                                <td className="px-5 py-3 text-xs">
                                    <span className={`px-2 py-0.5 rounded-full ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>
                                        {u.active ? t("common.active", "Active") : t("common.inactive", "Inactive")}
                                    </span>
                                </td>
                                <td className="px-5 py-3 text-right">
                                    <GhostButton onClick={() => setOpen(u)} testId={`user-edit-${u.id}`}><Pencil className="h-4 w-4" /></GhostButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <UserDialog open={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} restaurants={restaurants} />
        </div>
    );
}

function UserDialog({ open, onClose, onSaved, restaurants }) {
    const { t } = useTranslation();
    const isCreate = open === "create";
    const [form, setForm] = useState({});
    useEffect(() => {
        if (isCreate) setForm({ name: "", email: "", password: "", role: "restaurant", restaurant_id: "", active: true });
        else if (open) setForm({
            name: open.name || "", email: open.email || "", password: "",
            role: open.role, restaurant_id: open.restaurant_id || "",
            active: open.active ?? true,
        });
    }, [open, isCreate]);
    const save = async () => {
        if (!form.name?.trim() || !form.email?.trim()) {
            toast.error(t("settings.nameAndEmailAreRequired", "Name and email are required")); return;
        }
        if (isCreate && !form.password) { toast.error(t("settings.passwordRequired", "Password required")); return; }
        try {
            if (isCreate) await api.post("/users", form);
            else {
                const body = { ...form };
                if (!body.password) delete body.password;
                await api.patch(`/users/${open.id}`, body);
            }
            toast.success(t("settings.userSaved", "User saved"));
            onSaved();
        } catch (err) {
            toast.error((err?.response?.data?.detail != null ? apiErrorMessage(err) : t("settings.couldNotSaveUser", "Could not save user")));
        }
    };
    return (
        <Dialog open={!!open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="user-dialog" className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>{isCreate ? t("settings.newUser", "New user") : t("settings.editUser", "Edit user")}</DialogTitle>
                    <DialogDescription>{t("settings.userDialogDescription", "Add or edit an operational user in your company (Restaurant Admin, Head Chef, Kitchen Staff).")}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label={t("settings.name", "Name *")}><Input testId="user-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} /></Field>
                    <Field label={t("settings.email", "Email *")}><Input testId="user-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" /></Field>
                    <Field label={isCreate ? t("settings.password", "Password *") : t("settings.newPasswordLeaveBlankToKeep", "New password (leave blank to keep)")}>
                        <Input testId="user-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} type="password" />
                    </Field>
                    <Field label={t("common.role", "Role")}>
                        <select data-testid="user-role" value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white">
                            <option value="restaurant">{t("common.restaurant", "Restaurant")}</option>
                        </select>
                    </Field>
                    {form.role === "restaurant" && (
                        <Field label={t("common.restaurant", "Restaurant")}>
                            <select data-testid="user-restaurant_id" value={form.restaurant_id}
                                onChange={(e) => setForm({ ...form, restaurant_id: e.target.value })}
                                className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white">
                                <option value="">{t("common.selectPlaceholder", "— Select —")}</option>
                                {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                        </Field>
                    )}
                    <Field label={t("common.status", "Status")}>
                        <label className="inline-flex items-center gap-2 mt-2 text-sm">
                            <input type="checkbox" checked={!!form.active}
                                data-testid="user-active"
                                onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                            {t("common.active", "Active")}
                        </label>
                    </Field>
                </div>
                <DialogFooter>
                    <button data-testid="dialog-cancel-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("common.cancel", "Cancel")}</button>
                    <PrimaryButton onClick={save} testId="user-save">{t("common.save", "Save")}</PrimaryButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ---------- 4. Units ---------- */
function UnitsSection({ isCorporate }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ code: "", label: "", category: "weight" });
    const { confirm, ConfirmDialog } = useConfirm();
    const load = async () => setRows((await api.get("/units")).data);
    useEffect(() => { load(); }, []);
    const save = async () => {
        if (!form.code.trim()) { toast.error(t("settings.codeRequired", "Code required")); return; }
        try {
            await api.post("/units", form);
            toast.success(t("settings.unitAdded", "Unit added"));
            setCreating(false); setForm({ code: "", label: "", category: "weight" });
            load();
        } catch (err) {
            toast.error((err?.response?.data?.detail != null ? apiErrorMessage(err) : t("settings.couldNotCreateUnit", "Could not create unit")));
        }
    };
    const remove = async (u) => {
        const ok = await confirm({ title: t("settings.archiveUnitQ", { defaultValue: "Archive unit \"{{code}}\"?", code: u.code }), description: t("settings.archiveUnitHint", "It will no longer appear in pickers."), confirmLabel: t("common.archive", "Archive"), destructive: true });
        if (!ok) return;
        await api.delete(`/units/${u.id}`);
        toast.success(t("settings.unitArchived", "Unit archived"));
        load();
    };
    return (
        <div data-testid="units-section">
            <SectionHeader title={t("settings.units", "Units")}
                description={t("settings.configurableUnitsUsedAcrossRecipesInventory", "Configurable units used across recipes, inventory and deliveries.")}
                action={isCorporate && <PrimaryButton onClick={() => setCreating(true)} testId="unit-new"><Plus className="h-4 w-4" /> {t("settings.addUnit", "Add unit")}</PrimaryButton>} />
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm" data-testid="units-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{t("common.code", "Code")}</th>
                            <th className="px-5 py-3 font-semibold">{t("common.label", "Label")}</th>
                            <th className="px-5 py-3 font-semibold">{t("common.category", "Category")}</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.map((u) => (
                            <tr key={u.id} data-testid={`unit-row-${u.code}`}>
                                <td className="px-5 py-3 font-mono font-semibold">{u.code}</td>
                                <td className="px-5 py-3 text-neutral-600">{u.label}</td>
                                <td className="px-5 py-3 text-neutral-600 capitalize">{u.category}</td>
                                <td className="px-5 py-3 text-right">
                                    {isCorporate && (
                                        <button data-testid={`unit-delete-${u.code}`} onClick={() => remove(u)} className="text-red-600 hover:bg-red-50 rounded h-9 w-9 inline-flex items-center justify-center">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={creating} onOpenChange={(v) => !v && setCreating(false)}>
                <DialogContent data-testid="unit-create-dialog">
                    <DialogHeader><DialogTitle>{t("settings.addUnit", "Add unit")}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label={t("settings.code", "Code *")}><Input testId="unit-code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} /></Field>
                        <Field label={t("common.label", "Label")}><Input testId="unit-label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} /></Field>
                        <Field label={t("common.category", "Category")}>
                            <select data-testid="unit-category" value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white">
                                <option value="weight">{t("settings.weight", "Weight")}</option>
                                <option value="volume">{t("settings.volume", "Volume")}</option>
                                <option value="count">{t("settings.count", "Count")}</option>
                            </select>
                        </Field>
                    </div>
                    <DialogFooter>
                        <button data-testid="dialog-cancel-button" onClick={() => setCreating(false)} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("common.cancel", "Cancel")}</button>
                        <PrimaryButton onClick={save} testId="unit-save">{t("common.save", "Save")}</PrimaryButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <ConfirmDialog />
        </div>
    );
}

/* ---------- 5. Categories (product/recipe/supplier) ---------- */
function CategoriesSection({ isCorporate }) {
    const { t } = useTranslation();
    const [type, setType] = useState("product");
    const [rows, setRows] = useState([]);
    const [name, setName] = useState("");
    const { confirm, ConfirmDialog } = useConfirm();
    const load = useCallback(async () => setRows((await api.get(`/categories?type=${type}`)).data), [type]);
    useEffect(() => { load(); }, [load]);
    const add = async () => {
        if (!name.trim()) return;
        try {
            await api.post("/categories", { type, name: name.trim() });
            setName(""); toast.success(t("settings.categoryAdded", "Category added")); load();
        } catch (err) { toast.error((err?.response?.data?.detail != null ? apiErrorMessage(err) : t("settings.couldNotAdd", "Could not add"))); }
    };
    const remove = async (c) => {
        const ok = await confirm({ title: t("settings.archiveNamedQ", { defaultValue: "Archive \"{{name}}\"?", name: c.name }), confirmLabel: t("common.archive", "Archive"), destructive: true });
        if (!ok) return;
        await api.delete(`/categories/${c.id}`);
        toast.success(t("settings.categoryArchived", "Category archived")); load();
    };
    return (
        <div data-testid="categories-section">
            <SectionHeader title={t("settings.categories", "Categories")}
                description={t("settings.configurableCategoriesForProductsRecipesAnd", "Configurable categories for products, recipes and suppliers.")} />
            <div className="flex gap-2 mb-4" data-testid="category-type-tabs">
                {[["product", "Products"], ["recipe", "Recipes"], ["supplier", "Suppliers"]].map(([v, label]) => (
                    <button key={v} data-testid={`category-tab-${v}`}
                        onClick={() => setType(v)}
                        className={`h-10 px-4 inline-flex items-center gap-2 rounded-md text-sm font-semibold ${type === v ? "bg-neutral-900 text-white" : "border border-neutral-200 text-neutral-700 hover:bg-neutral-50"}`}>
                        {t(`settings.categoryType.${v}`, label)}
                    </button>
                ))}
            </div>
            {isCorporate && (
                <div className="flex gap-2 mb-4">
                    <Input testId="category-name" value={name} onChange={setName} placeholder={t(`settings.newCategory.${type}`, `New ${type} category`)} />
                    <PrimaryButton onClick={add} testId="category-add"><Plus className="h-4 w-4" /> {t("common.add", "Add")}</PrimaryButton>
                </div>
            )}
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm" data-testid="categories-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{t("common.name", "Name")}</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 ? (
                            <tr><td colSpan={2} className="px-5 py-6 text-sm text-neutral-500 text-center">{t("settings.noCategoriesYet", "No categories yet.")}</td></tr>
                        ) : rows.map((c) => (
                            <tr key={c.id} data-testid={`category-row-${c.id}`}>
                                <td className="px-5 py-3 font-medium">{c.name}</td>
                                <td className="px-5 py-3 text-right">
                                    {isCorporate && (
                                        <button data-testid={`category-delete-${c.id}`} onClick={() => remove(c)} className="text-red-600 hover:bg-red-50 rounded h-9 w-9 inline-flex items-center justify-center">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ConfirmDialog />
        </div>
    );
}

/* ---------- 6. Storage areas (restaurant scope) ---------- */
function AreasSection() {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const [rows, setRows] = useState([]);
    const [name, setName] = useState("");
    const { confirm, ConfirmDialog } = useConfirm();
    const load = useCallback(async () => {
        const params = user.role === "corporate" && restaurantId ? { restaurant_id: restaurantId } : {};
        setRows((await api.get("/inventory/areas", { params })).data);
    }, [restaurantId, user.role]);
    useEffect(() => { load(); }, [load]);
    const add = async () => {
        if (!name.trim()) return;
        try {
            await api.post("/inventory-areas",
                { name: name.trim(), restaurant_id: restaurantId });
            setName(""); toast.success(t("settings.areaAdded", "Area added")); load();
        } catch { toast.error(t("settings.couldNotAddArea", "Could not add area")); }
    };
    const remove = async (a) => {
        const ok = await confirm({ title: t("settings.archiveAreaQ", { defaultValue: "Archive area \"{{name}}\"?", name: a.name }), confirmLabel: t("common.archive", "Archive"), destructive: true });
        if (!ok) return;
        await api.delete(`/inventory-areas/${a.id}`);
        toast.success(t("settings.areaArchived", "Area archived")); load();
    };
    return (
        <div data-testid="areas-section">
            <SectionHeader title={t("settings.storageAreas", "Storage areas")}
                description={t("settings.inventoryLocationsSpecificToThisRestaurant", "Inventory locations specific to this restaurant.")} />
            <div className="flex gap-2 mb-4">
                <Input testId="area-name" value={name} onChange={setName} placeholder={t("settings.eGWalkInFridge", "e.g. Walk-in fridge")} />
                <PrimaryButton onClick={add} testId="area-add"><Plus className="h-4 w-4" /> {t("common.add", "Add")}</PrimaryButton>
            </div>
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm" data-testid="areas-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{t("common.name", "Name")}</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 ? (
                            <tr><td colSpan={2} className="px-5 py-6 text-sm text-neutral-500 text-center">{t("settings.noAreasConfiguredYet", "No areas configured yet.")}</td></tr>
                        ) : rows.map((a) => (
                            <tr key={a.id} data-testid={`area-row-${a.id}`}>
                                <td className="px-5 py-3 font-medium">{a.name}</td>
                                <td className="px-5 py-3 text-right">
                                    <button data-testid={`area-delete-${a.id}`} onClick={() => remove(a)} className="text-red-600 hover:bg-red-50 rounded h-9 w-9 inline-flex items-center justify-center">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ConfirmDialog />
        </div>
    );
}

/* ---------- 7. Equipment ---------- */
function EquipmentSection() {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const [rows, setRows] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: "", equipment_type: "fridge", target_min_temp: 0, target_max_temp: 5 });
    const { confirm, ConfirmDialog } = useConfirm();
    const load = useCallback(async () => {
        const params = user.role === "corporate" && restaurantId ? { restaurant_id: restaurantId } : {};
        setRows((await api.get("/equipment", { params })).data);
    }, [restaurantId, user.role]);
    useEffect(() => { load(); }, [load]);
    const save = async () => {
        if (!form.name.trim()) { toast.error(t("settings.nameRequired", "Name required")); return; }
        try {
            await api.post("/equipment", { ...form, restaurant_id: restaurantId });
            setCreating(false);
            setForm({ name: "", equipment_type: "fridge", target_min_temp: 0, target_max_temp: 5 });
            toast.success(t("settings.equipmentAdded", "Equipment added")); load();
        } catch { toast.error(t("settings.couldNotAddEquipment", "Could not add equipment")); }
    };
    const remove = async (e) => {
        const ok = await confirm({ title: t("settings.archiveEquipmentQ", { defaultValue: "Archive equipment \"{{name}}\"?", name: e.name }), confirmLabel: t("common.archive", "Archive"), destructive: true });
        if (!ok) return;
        await api.delete(`/equipment/${e.id}`);
        toast.success(t("settings.equipmentArchived", "Equipment archived")); load();
    };
    return (
        <div data-testid="equipment-section">
            <SectionHeader title={t("settings.equipment", "Equipment")}
                description={t("settings.fridgesFreezersAndUnitsUsedFor", "Fridges, freezers and units used for Daily Checks.")}
                action={<PrimaryButton onClick={() => setCreating(true)} testId="equipment-new"><Plus className="h-4 w-4" /> {t("settings.addEquipment", "Add equipment")}</PrimaryButton>} />
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm" data-testid="equipment-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{t("common.name", "Name")}</th>
                            <th className="px-5 py-3 font-semibold">{t("common.type", "Type")}</th>
                            <th className="px-5 py-3 font-semibold">{t("settings.targetRange", "Target range")}</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 ? (
                            <tr><td colSpan={4} className="px-5 py-6 text-sm text-neutral-500 text-center">{t("settings.noEquipmentConfiguredYet", "No equipment configured yet.")}</td></tr>
                        ) : rows.map((e) => (
                            <tr key={e.id} data-testid={`equipment-row-${e.id}`}>
                                <td className="px-5 py-3 font-medium">{e.name}</td>
                                <td className="px-5 py-3 text-neutral-600 capitalize">{e.equipment_type}</td>
                                <td className="px-5 py-3 text-neutral-600 tabular-nums">{e.target_min_temp ?? "—"}°C ↔ {e.target_max_temp ?? "—"}°C</td>
                                <td className="px-5 py-3 text-right">
                                    <button data-testid={`equipment-delete-${e.id}`} onClick={() => remove(e)} className="text-red-600 hover:bg-red-50 rounded h-9 w-9 inline-flex items-center justify-center">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Dialog open={creating} onOpenChange={(v) => !v && setCreating(false)}>
                <DialogContent data-testid="equipment-create-dialog">
                    <DialogHeader><DialogTitle>{t("settings.addEquipment", "Add equipment")}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label={t("settings.name", "Name *")}><Input testId="eq-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} /></Field>
                        <Field label={t("common.type", "Type")}>
                            <select data-testid="eq-type" value={form.equipment_type}
                                onChange={(e) => setForm({ ...form, equipment_type: e.target.value })}
                                className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white">
                                {["fridge", "freezer", "hot_holding", "cooling_area", "other"].map((v) => (
                                    <option key={v} value={v}>{v.replace("_", " ")}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label={t("settings.targetMinC", "Target min (°C)")}><Input testId="eq-min" value={form.target_min_temp} onChange={(v) => setForm({ ...form, target_min_temp: parseFloat(v) || 0 })} type="number" /></Field>
                        <Field label={t("settings.targetMaxC", "Target max (°C)")}><Input testId="eq-max" value={form.target_max_temp} onChange={(v) => setForm({ ...form, target_max_temp: parseFloat(v) || 0 })} type="number" /></Field>
                    </div>
                    <DialogFooter>
                        <button data-testid="dialog-cancel-button" onClick={() => setCreating(false)} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("common.cancel", "Cancel")}</button>
                        <PrimaryButton onClick={save} testId="eq-save">{t("common.save", "Save")}</PrimaryButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <ConfirmDialog />
        </div>
    );
}

/* ---------- 7b. Operating Days & Daily Check Days (independent) --- */
const WEEK_DAYS = [
    { key: "mon", label: "Monday", labelKey: "settings.monday" },
    { key: "tue", label: "Tuesday", labelKey: "settings.tuesday" },
    { key: "wed", label: "Wednesday", labelKey: "settings.wednesday" },
    { key: "thu", label: "Thursday", labelKey: "settings.thursday" },
    { key: "fri", label: "Friday", labelKey: "settings.friday" },
    { key: "sat", label: "Saturday", labelKey: "settings.saturday" },
    { key: "sun", label: "Sunday", labelKey: "settings.sunday" },
];
const DEFAULT_SCHEDULE = { mon: true, tue: true, wed: true, thu: true, fri: true, sat: true, sun: true };

function ScheduleCard({ testIdBase, title, description, endpoint,
                          fieldName, successLabel }) {
    const { t } = useTranslation();
    const { restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get(endpoint, { params }).then(({ data }) => {
            const sch = { ...DEFAULT_SCHEDULE, ...(data?.[fieldName] || {}) };
            setSchedule(sch);
            setLoaded(true);
        }).catch(() => setLoaded(true));
    }, [restaurantId, endpoint, fieldName, params]);

    const toggleDay = (k) => setSchedule((s) => ({ ...s, [k]: !s[k] }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put(endpoint, { ...schedule }, { params });
            toast.success(successLabel);
        } catch { toast.error(t("settings.saveFailed", "Save failed")); }
        finally { setSaving(false); }
    };

    if (!loaded) return <div className="kop-card p-6 text-sm">{t("common.loading", "Loading…")}</div>;

    return (
        <div data-testid={`${testIdBase}-section`}>
            <SectionHeader title={title} description={description} />
            <div className="kop-card p-6 space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {WEEK_DAYS.map((d) => (
                        <label key={d.key}
                            data-testid={`${testIdBase}-day-${d.key}`}
                            className={`flex items-center gap-2 border rounded-md px-3 h-11 cursor-pointer ${schedule[d.key] ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 bg-white text-neutral-700"}`}>
                            <input type="checkbox" className="sr-only"
                                checked={schedule[d.key]}
                                onChange={() => toggleDay(d.key)} />
                            <span className="text-sm font-semibold">{t(d.labelKey, d.label)}</span>
                            <span className="ml-auto text-xs">{schedule[d.key] ? "✓" : "✗"}</span>
                        </label>
                    ))}
                </div>
                <div className="border-t border-neutral-100 pt-4 flex items-center justify-end">
                    <PrimaryButton onClick={save} testId={`${testIdBase}-save`} disabled={saving}>
                        {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
                    </PrimaryButton>
                </div>
            </div>
        </div>
    );
}

function OperatingDaysSection() {
    const { t } = useTranslation();
    return (
        <div className="space-y-8">
            <ScheduleCard
                testIdBase="operating-days"
                title={t("settings.operatingDays", "Operating Days")}
                description={t("settings.chooseTheDaysThisRestaurantNormally", "Choose the days this restaurant normally operates. Used for the 'Avg expense / operating day' report metric.")}
                endpoint="/restaurants/operating-days"
                fieldName="operating_days"
                successLabel={t("settings.operatingDaysSaved", "Operating days saved")}
            />
            <ScheduleCard
                testIdBase="daily-check-days"
                title={t("settings.dailyCheckDays", "Daily Check Days")}
                description={t("settings.chooseWhichDaysThisRestaurantRequires", "Choose which days this restaurant requires Daily Checks. Independent from Operating Days.")}
                endpoint="/daily-checks/schedule"
                fieldName="schedule"
                successLabel={t("settings.dailyCheckDaysSaved", "Daily Check days saved")}
            />
            <div className="text-xs text-neutral-500">
                {t("settings.skipDayTip", "Tip: For exceptional closures (holidays, vacations, maintenance) use the Skip Day button in Daily Checks.")}
            </div>
        </div>
    );
}

/* ---------- 8. Inventory Settings ---------- */
function InventorySettingsSection() {
    const { t } = useTranslation();
    const [settings, setSettings] = useState(null);
    useEffect(() => {
        api.get("/settings/inventory").then(({ data }) => setSettings(data));
    }, []);
    if (!settings) return <div className="kop-card p-6 text-sm">{t("common.loading", "Loading…")}</div>;
    const save = async () => {
        try {
            await api.patch("/settings/inventory", settings);
            toast.success(t("settings.inventorySettingsUpdated", "Inventory settings updated"));
        } catch { toast.error(t("common.couldNotSave", "Could not save")); }
    };
    return (
        <div data-testid="inventory-settings-section">
            <SectionHeader title={t("settings.inventorySettings", "Inventory settings")}
                description={t("settings.frequencyPeriodTypeAndAutoArchive", "Frequency, period type and auto-archive behaviour.")} />
            <div className="kop-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("settings.inventoryPeriod", "Inventory period")}>
                    <select data-testid="inv-period_type" value={settings.inventory_period_type}
                        onChange={(e) => setSettings({ ...settings, inventory_period_type: e.target.value })}
                        className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white">
                        <option value="monthly">{t("settings.monthly", "Monthly")}</option>
                        <option value="weekly">{t("settings.weekly", "Weekly")}</option>
                    </select>
                </Field>
                <Field label={t("settings.frequencyDays", "Frequency (days)")} hint={t("settings.howOftenANewInventoryCount", "How often a new inventory count is expected.")}>
                    <Input testId="inv-frequency_days" value={settings.inventory_frequency_days} onChange={(v) => setSettings({ ...settings, inventory_frequency_days: parseInt(v) || 0 })} type="number" />
                </Field>
                <Field label={t("settings.defaultInventoryDay", "Default inventory day")}>
                    <Input testId="inv-default_day" value={settings.default_inventory_day} onChange={(v) => setSettings({ ...settings, default_inventory_day: v })} placeholder="last_day_of_month" />
                </Field>
                <Field label={t("settings.autoArchiveAfterDays", "Auto-archive after (days)")} hint={t("settings.productsNotUsedCountedOrDelivered", "Products not used, counted or delivered for this many days will be archived. Never permanently deleted.")}>
                    <Input testId="inv-auto_archive" value={settings.auto_archive_after_days} onChange={(v) => setSettings({ ...settings, auto_archive_after_days: parseInt(v) || 0 })} type="number" />
                </Field>
                <div className="md:col-span-2 flex justify-end">
                    <PrimaryButton onClick={save} testId="inv-save">{t("common.saveChanges", "Save changes")}</PrimaryButton>
                </div>
            </div>
            {/* Batch C · Product Unit Review entry point (2026-02). */}
            <div className="kop-card p-6 mt-4">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <div className="font-semibold">{t("settings.productUnitReview", "Product unit review")}</div>
                        <div className="text-sm text-neutral-600 mt-1">
                            {t("settings.unitReviewHint", "Confirm the canonical physical unit for products whose unit could not be established from invoices (e.g. sacks invoiced as \"kpl\" that actually contain kilograms).")}
                        </div>
                    </div>
                    <a href="/settings/unit-review"
                       data-testid="open-unit-review"
                       className="inline-flex items-center h-10 px-4 rounded-md border border-neutral-300 bg-white text-sm font-medium hover:bg-neutral-50">
                        {t("settings.openUnitReview", "Open Unit Review")}
                    </a>
                </div>
            </div>
        </div>
    );
}

/* ---------- 9. Recipe Settings ---------- */
function RecipeSettingsSection() {
    const { t } = useTranslation();
    return (
        <div data-testid="recipe-settings-section">
            <SectionHeader title={t("settings.recipeSettings", "Recipe settings")}
                description={t("settings.recipeCategoriesAreManagedUnderThe", "Recipe categories are managed under the Categories section. Recipes remain flat ingredient tables — nested components are not supported in MVP.")} />
            <div className="kop-card p-6 text-sm text-neutral-600 space-y-3">
                <p><strong>{t("settings.corporateVsLocalRecipes", "Corporate vs Local recipes:")}</strong> {t("settings.corporateUsersCreateCompanyWideRecipes", "Corporate users create company-wide recipes that all restaurants can use. Restaurant users create local recipes that only their restaurant sees.")}</p>
                <p><strong>{t("settings.baseYieldUnits", "Base yield units:")}</strong> {t("settings.baseYieldUnitsText", "defined per recipe (portion / batch / tray / kg / L) — configurable in the Units section.")}</p>
                <p className="text-xs text-neutral-400 italic">{t("settings.recipeSettingsDocOnly", "Settings here are for documentation only; recipes themselves are managed in the Recipes module.")}</p>
            </div>
        </div>
    );
}

/* ---------- 10. Integrations ---------- */
function IntegrationsSection() {
    const { t } = useTranslation();
    const [data, setData] = useState({});
    useEffect(() => { api.get("/settings/integrations").then(({ data }) => setData(data)); }, []);
    const items = [
        { key: "ocr_provider", label: t("settings.ocrProvider", "OCR provider"), hint: t("settings.autoExtractDataFromDeliveryPhotos", "Auto-extract data from delivery photos / PDFs.") },
        { key: "barcode_lookup", label: t("settings.barcodeLookup", "Barcode lookup"), hint: t("settings.externalDatabaseForUnknownBarcodes", "External database for unknown barcodes.") },
        { key: "file_storage", label: t("settings.fileStorage", "File storage"), hint: t("settings.whereUploadedDeliveryDocumentsAreKept", "Where uploaded delivery documents are kept.") },
        { key: "ai_assistant", label: t("settings.aiAssistant", "AI assistant"), hint: t("settings.conversationalHelperForMenusAndReports", "Conversational helper for menus and reports.") },
        { key: "pos", label: t("settings.posIntegration", "POS integration"), hint: t("settings.notPlannedMiseoneIsNotA", "Not planned — MiseOne is not a POS.") },
    ];
    return (
        <div data-testid="integrations-section">
            <SectionHeader title={t("settings.integrations", "Integrations")}
                description={t("settings.externalServicesThatConnectToMiseone", "External services that connect to MiseOne. Most are placeholders for future work.")} />
            <div className="kop-card divide-y divide-neutral-100">
                {items.map((it) => {
                    const v = data[it.key] || {};
                    const tone = v.status === "active" ? "bg-emerald-50 text-emerald-700"
                        : v.status === "placeholder" ? "bg-neutral-100 text-neutral-500"
                        : "bg-red-50 text-red-600";
                    return (
                        <div key={it.key} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`integration-${it.key}`}>
                            <div>
                                <div className="text-sm font-semibold">{it.label}</div>
                                <div className="text-xs text-neutral-500 mt-0.5">{it.hint}</div>
                            </div>
                            <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${tone}`}>
                                {(v.status || "n/a").replace("_", " ")}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ---------- 11. Branding ---------- */
function BrandingSection() {
    const { t } = useTranslation();
    const [b, setB] = useState(null);
    useEffect(() => { api.get("/settings/branding").then(({ data }) => setB(data)); }, []);
    const save = async () => {
        try {
            await api.patch("/settings/branding", b);
            toast.success(t("settings.brandingSavedPreviewOnlyFullTheming", "Branding saved (preview only — full theming coming soon)."));
        } catch { toast.error(t("common.couldNotSave", "Could not save")); }
    };
    if (!b) return <div className="kop-card p-6 text-sm">{t("common.loading", "Loading…")}</div>;
    return (
        <div data-testid="branding-section">
            <SectionHeader title={t("settings.branding", "Branding")}
                description={t("settings.customiseTheAppAppearanceForYour", "Customise the app appearance for your kitchen group. Full theming will arrive in a future release.")} />
            <div className="kop-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("settings.appName", "App name")}><Input testId="brand-app_name" value={b.app_name} onChange={(v) => setB({ ...b, app_name: v })} /></Field>
                <Field label={t("settings.logoUrl", "Logo URL")}><Input testId="brand-logo_url" value={b.logo_url} onChange={(v) => setB({ ...b, logo_url: v })} /></Field>
                <Field label={t("settings.primaryColor", "Primary color")}><Input testId="brand-primary_color" value={b.primary_color} onChange={(v) => setB({ ...b, primary_color: v })} placeholder="#111111" /></Field>
                <Field label={t("settings.secondaryColor", "Secondary color")}><Input testId="brand-secondary_color" value={b.secondary_color} onChange={(v) => setB({ ...b, secondary_color: v })} placeholder="#737373" /></Field>
                <Field label={t("settings.accentColor", "Accent color")}><Input testId="brand-accent_color" value={b.accent_color} onChange={(v) => setB({ ...b, accent_color: v })} placeholder="#fbbf24" /></Field>
                <div className="md:col-span-2">
                    <div className="text-[10px] uppercase tracking-wide font-semibold text-neutral-600 mb-2">{t("settings.preview", "Preview")}</div>
                    <div className="flex items-center gap-4 p-4 border border-neutral-200 rounded-md">
                        <img src={b.logo_url} alt="" className="h-12 w-auto" />
                        <div className="flex gap-2">
                            <span className="h-8 w-8 rounded border" style={{ background: b.primary_color }} />
                            <span className="h-8 w-8 rounded border" style={{ background: b.secondary_color }} />
                            <span className="h-8 w-8 rounded border" style={{ background: b.accent_color }} />
                        </div>
                    </div>
                </div>
                <div className="md:col-span-2 flex justify-end">
                    <PrimaryButton onClick={save} testId="brand-save">{t("common.saveChanges", "Save changes")}</PrimaryButton>
                </div>
            </div>
        </div>
    );
}

// Launch Fix R8 — Appearance section (Light / Dark / System).
function AppearanceSection() {
    const { t } = useTranslation();
    const { theme, setTheme } = useTheme();
    const OPTIONS = [
        { key: "light",  label: t("settings.light", "Light"),  icon: Sun,     sub: t("settings.brightInterfaceIdealForDaytimeService", "Bright interface, ideal for daytime service.") },
        { key: "dark",   label: t("settings.dark", "Dark"),   icon: Moon,    sub: t("settings.charcoalPaletteEasierOnTheEyes", "Charcoal palette. Easier on the eyes at night.") },
        { key: "system", label: t("settings.system", "System"), icon: Monitor, sub: t("settings.followsYourDeviceBrowserPreference", "Follows your device / browser preference.") },
    ];
    return (
        <section data-testid="settings-appearance" className="space-y-6">
            <div>
                <h3 className="font-display text-2xl font-bold text-neutral-900">{t("settings.appearance", "Appearance")}</h3>
                <p className="text-sm text-neutral-500 mt-1">
                    {t("settings.appearanceIntro", "Choose how MiseOne looks on this device. Your choice is saved locally and persists across refresh and browser restarts.")}
                </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
                {OPTIONS.map((o) => {
                    const selected = theme === o.key;
                    return (
                        <button
                            key={o.key}
                            data-testid={`appearance-option-${o.key}`}
                            onClick={() => setTheme(o.key)}
                            aria-pressed={selected}
                            className={`text-left rounded-lg p-4 border transition ${
                                selected
                                    ? "border-neutral-900 bg-neutral-50 shadow-sm"
                                    : "border-neutral-200 hover:border-neutral-300"
                            }`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <o.icon className="h-5 w-5" />
                                {selected && (
                                    <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-neutral-900 text-white">
                                        {t("settings.selected", "Selected")}
                                    </span>
                                )}
                            </div>
                            <div className="font-display font-bold text-neutral-900">{o.label}</div>
                            <div className="text-xs text-neutral-500 mt-1 leading-snug">{o.sub}</div>
                        </button>
                    );
                })}
            </div>
            <div className="text-xs text-neutral-500 italic">
                {t("settings.printLightNote", "Print and PDF documents always use the light layout so invoices, reports and menus print correctly regardless of your on-screen preference.")}
            </div>
        </section>
    );
}

